import { ErrorSnackbar } from '@application/components';
import { FeedbackContext, UserContext } from '@application/contexts';
import { downloadRemoteFile } from '@application/utils/file-utils';
import { extractErrorCodes } from '@application/utils/urql-utils';
import { useGenerateSingleAddressSearchMutation } from '@domain/index';
import { ReportAggregation } from '@domain/graphql.types';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useSiteLookupFilters from '../filters/useSiteLookupFilters';
import { getAvailableSiteLookupDataViews, SiteLookupDataView, SiteLookupDataViewValue } from '../siteLookupDataView';

const useSiteLookup = () => {
  const { t } = useTranslation();

  const { refreshUser, tenant } = useContext(UserContext);
  const { setFeedback } = useContext(FeedbackContext);

  const { filters, updateFilter } = useSiteLookupFilters();
  const hasHistoricalAccess = !!tenant?.permissions.apiPermissions?.historicalViewAccess;
  const hasRealTimeAccess = !!tenant?.permissions.apiPermissions?.realTimeAccess;
  const maxHistoricalMonths = tenant?.permissions.maxHistoricalMonths ?? 3;

  const { data, error, fetching, generateReport } = useGenerateSingleAddressSearchMutation();
  const [dataView, setDataView] = useState(SiteLookupDataViewValue.historical);
  const availableDataViews = useMemo(
    () => getAvailableSiteLookupDataViews({ hasHistoricalAccess, hasRealTimeAccess }),
    [hasHistoricalAccess, hasRealTimeAccess],
  );

  useEffect(() => {
    if (!availableDataViews.length || availableDataViews.includes(dataView)) {
      return;
    }

    setDataView(availableDataViews[0]);
  }, [availableDataViews, dataView]);

  const downloadReport = useCallback(
    (aggregation: ReportAggregation, months: number) => {
      if (!filters?.address) {
        return;
      }

      generateReport({
        input: {
          aggregation,
          coordinates: filters.address?.coordinates || { latitude: 0, longitude: 0 },
          fullAddress: filters.address?.fullAddress,
          months,
          range: filters.radius,
          stateCode: filters.address?.regionCode || '',
        },
      });
    },
    [filters, generateReport],
  );

  const setDataViewSafely = useCallback(
    (nextView: SiteLookupDataView) => {
      if (availableDataViews.includes(nextView)) {
        setDataView(nextView);
      }
    },
    [availableDataViews],
  );

  const resolvedDataView = useMemo(() => {
    if (availableDataViews.includes(dataView)) {
      return dataView;
    }

    return availableDataViews[0] || SiteLookupDataViewValue.realtime;
  }, [availableDataViews, dataView]);

  useEffect(() => {
    if (!data?.generateSingleAddressSearchReport.link) {
      return;
    }

    downloadRemoteFile(data.generateSingleAddressSearchReport.link);
    refreshUser();
  }, [data, refreshUser]);

  useEffect(() => {
    const errors = extractErrorCodes(error);

    if (!errors.length) {
      return;
    }

    setFeedback(
      <ErrorSnackbar
        message={t('siteLookup.error.downloadReport.message')}
        onClose={() => setFeedback(null)}
        title={t('siteLookup.error.downloadReport.title')}
      />,
    );
  }, [error, setFeedback, t]);

  return {
    actions: {
      downloadReport,
      setDataView: setDataViewSafely,
      updateFilter,
    },
    state: {
      dataView: resolvedDataView,
      filters,
      hasHistoricalAccess,
      hasRealTimeAccess,
      isDownloadingSitesReport: fetching,
      maxHistoricalMonths,
      remainingDownloads: tenant?.remainingDownloads || 0,
    },
  };
};

export default useSiteLookup;
